import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import MarketingShell from "./MarketingShell";
import { ArrowsClockwise, MagnifyingGlass, Trash, Broom, PencilSimple, Plus, X } from "@phosphor-icons/react";

// Visual identity for the "Type" column chip.
const KIND_CHIP = {
  client:     { bg: "#F0FDF4", fg: "#16A34A", border: "#BBF7D0", label: "Client" },
  crm_lead:   { bg: "#F5F3FF", fg: "#7C3AED", border: "#DDD6FE", label: "Lead" },
  csv_import: { bg: "#EFF6FF", fg: "#0055FF", border: "#BFDBFE", label: "CSV" },
};

export default function ContactsPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [suppressed, setSuppressed] = useState("");
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [purging, setPurging] = useState(false);
  // `editing` is null when closed. Set to `{}` for a new contact,
  // or to an existing contact object for edit.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Open the edit modal — always re-fetches the FULL contact detail so the
  // form is seeded with every field (phone / cell_phone / job_title are
  // omitted from the list endpoint to keep that payload light, which used
  // to make these fields look "blank after saving"). For a brand-new
  // contact pass `null`/`undefined` and we just seed an empty object.
  const openEditor = async (rowOrNull) => {
    if (!rowOrNull) {
      setEditing({});
      return;
    }
    // Optimistic open with whatever we have, then enrich.
    setEditing({ ...rowOrNull });
    try {
      const r = await api.get(`/marketing/contacts/${rowOrNull.id}`);
      // Only overwrite if the user hasn't already started typing.
      setEditing((cur) => (cur && cur.id === rowOrNull.id ? { ...cur, ...r.data } : cur));
    } catch {
      /* keep the optimistic open; the list-row data is still better than nothing */
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset });
      if (q) params.set("q", q);
      if (source) params.set("source_type", source);
      if (suppressed) params.set("suppressed", suppressed);
      const r = await api.get(`/marketing/contacts?${params}`);
      setRows(r.data.items);
      setTotal(r.data.total);
    } catch { toast.error("Failed to load contacts"); }
    setLoading(false);
  };
  useEffect(() => { reload(); }, [offset, source, suppressed]); // eslint-disable-line

  const runSync = async (kind) => {
    setSyncing(true);
    try {
      const r = await api.post(`/marketing/contacts/sync/${kind}`);
      toast.success(`${kind}: +${r.data.created} new, ${r.data.updated} updated`);
      reload();
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };

  const toggleSuppress = async (contact) => {
    const next = !contact.is_suppressed;
    // Optimistic update for snappy UX.
    setRows(rows.map(r => r.id === contact.id ? { ...r, is_suppressed: next } : r));
    try {
      await api.post(`/marketing/contacts/${contact.id}/suppress`, { suppress: next });
      toast.success(next ? `${contact.email} marked Do Not Send` : `${contact.email} can receive email again`);
    } catch (e) {
      // revert on error
      setRows(rows.map(r => r.id === contact.id ? { ...r, is_suppressed: !next } : r));
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteOne = (contact) => {
    toast(`Delete ${contact.email}?`, {
      description: "They'll be removed from the marketing list. You can re-add them via a CSV import or by running Sync Clients / Sync Leads.",
      duration: 10000,
      action: {
        label: "Delete",
        onClick: async () => {
          try {
            await api.delete(`/marketing/contacts/${contact.id}`);
            toast.success(`${contact.email} deleted`);
            reload();
          } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  const saveContact = async () => {
    if (!editing) return;
    const isNew = !editing.id;
    if (isNew && !editing.email?.trim()) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: editing.first_name || null,
        last_name: editing.last_name || null,
        full_name: editing.full_name || [editing.first_name, editing.last_name].filter(Boolean).join(" ") || null,
        company_name: editing.company_name || null,
        job_title: editing.job_title || null,
        phone: editing.phone || null,
        cell_phone: editing.cell_phone || null,
        country: editing.country || null,
        consent_status: editing.consent_status || "unknown",
      };
      if (isNew) {
        payload.email = editing.email.trim();
        payload.consent_source = "manual";
        await api.post(`/marketing/contacts`, payload);
        toast.success(`Added ${payload.email}`);
      } else {
        await api.put(`/marketing/contacts/${editing.id}`, payload);
        toast.success(`Updated ${editing.email}`);
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const purgeNonClients = () => {
    const visibleCandidates = rows.filter(r => r.primary_kind !== "client").length;
    toast.warning(`Delete every non-client contact?`, {
      description: `This permanently removes every lead and CSV contact in the marketing list. ${visibleCandidates} of ${rows.length} visible rows qualify — the real DB total is likely larger.`,
      duration: 15000,
      action: {
        label: "Delete All",
        onClick: async () => {
          setPurging(true);
          try {
            const r = await api.post(`/marketing/contacts/purge-non-clients`);
            toast.success(`Deleted ${r.data.deleted} non-client contact${r.data.deleted === 1 ? "" : "s"}`);
            setOffset(0);
            reload();
          } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
          setPurging(false);
        },
      },
      cancel: { label: "Cancel", onClick: () => {} },
    });
  };

  return (
    <MarketingShell>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
          <input value={q} onChange={e => setQ(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && (setOffset(0), reload())}
                 placeholder="Search by email, name, or company…"
                 className="h-9 pl-9 pr-3 text-sm border border-[#E5E5E5] w-full focus:outline-none focus:border-[#0055FF]"
                 data-testid="contact-search" />
        </div>
        <select value={source} onChange={e => { setOffset(0); setSource(e.target.value); }}
                className="h-9 px-2 text-xs border border-[#E5E5E5]" data-testid="source-filter">
          <option value="">All sources</option>
          <option value="csv_import">CSV imports</option>
          <option value="client">Clients</option>
          <option value="crm_lead">CRM leads</option>
        </select>
        <select value={suppressed} onChange={e => { setOffset(0); setSuppressed(e.target.value); }}
                className="h-9 px-2 text-xs border border-[#E5E5E5]" data-testid="supp-filter">
          <option value="">All contacts</option>
          <option value="false">Sendable only</option>
          <option value="true">Do Not Send only</option>
        </select>
        <div className="flex gap-1">
          <button onClick={() => runSync("clients")} disabled={syncing}
                  className="h-9 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] disabled:opacity-40 flex items-center gap-1"
                  data-testid="sync-clients">
            <ArrowsClockwise size={12} />Sync Clients
          </button>
          <button onClick={() => runSync("leads")} disabled={syncing}
                  className="h-9 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#0055FF] disabled:opacity-40 flex items-center gap-1"
                  data-testid="sync-leads">
            <ArrowsClockwise size={12} />Sync Leads
          </button>
          <button onClick={() => openEditor(null)}
                  className="h-9 px-3 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC] flex items-center gap-1"
                  data-testid="new-contact-btn">
            <Plus size={12} weight="bold" />New Contact
          </button>
          <button onClick={purgeNonClients} disabled={purging}
                  className="h-9 px-3 text-xs font-bold uppercase tracking-wider bg-[#DC2626] text-white hover:bg-[#B91C1C] disabled:opacity-40 flex items-center gap-1"
                  data-testid="purge-non-clients">
            <Broom size={12} />{purging ? "Purging…" : "Delete Non-Clients"}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5]">
        {loading ? (
          <p className="p-8 text-center text-sm text-[#A3A3A3]">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-10 text-center text-sm text-[#A3A3A3]">No contacts match. Try syncing clients/leads or importing a CSV.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Email</th>
                <th className="text-left px-3 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Company</th>
                <th className="text-left px-3 py-2 font-bold">Type</th>
                <th className="text-left px-3 py-2 font-bold">Country</th>
                <th className="text-left px-3 py-2 font-bold">Consent</th>
                <th className="text-center px-3 py-2 font-bold">Do Not Send</th>
                <th className="text-right px-5 py-2 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => {
                const chip = KIND_CHIP[c.primary_kind];
                // Clickable only if we have a destination record.
                //   client  → /clients/{client_id}
                //   lead    → /crm/leads/{lead_id}
                //   csv     → /marketing/imports/{batch_id}
                let href = null;
                if (c.primary_kind === "client" && c.primary_source_id) href = `/clients/${c.primary_source_id}`;
                else if (c.primary_kind === "crm_lead" && c.primary_source_id) href = `/crm/leads/${c.primary_source_id}`;
                else if (c.primary_kind === "csv_import" && c.primary_source_id) href = `/marketing/imports/${c.primary_source_id}`;
                const openSource = () => { if (href) nav(href); };
                return (
                  <tr key={c.id}
                      className={`border-t border-[#F5F5F5] hover:bg-[#FAFAFA] ${href ? "cursor-pointer" : ""} ${c.is_suppressed ? "bg-[#FFFBEB]/40" : ""}`}
                      data-testid={`contact-row-${c.id}`}>
                    <td onClick={openSource} className="px-5 py-2.5 font-semibold text-[#0A0A0A]">
                      {href ? <span className="hover:text-[#0055FF] hover:underline">{c.email}</span> : c.email}
                    </td>
                    <td onClick={openSource} className="px-3 py-2.5 text-[#525252]">{c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</td>
                    <td onClick={openSource} className="px-3 py-2.5 text-[#525252] max-w-xs truncate">{c.company_name || "—"}</td>
                    <td onClick={openSource} className="px-3 py-2.5">
                      {chip ? (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border"
                          style={{ backgroundColor: chip.bg, color: chip.fg, borderColor: chip.border }}
                          data-testid={`kind-chip-${c.primary_kind}`}
                        >
                          {chip.label}
                          {c.source_types && c.source_types.length > 1 && c.primary_kind === "client" && c.source_types.includes("crm_lead") && (
                            <span className="ml-1 font-normal opacity-70">+Lead</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[10px] text-[#A3A3A3]">—</span>
                      )}
                    </td>
                    <td onClick={openSource} className="px-3 py-2.5 text-[#525252]">{c.country || "—"}</td>
                    <td onClick={openSource} className="px-3 py-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#525252]">{c.consent_status || "unknown"}</span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <label className="inline-flex items-center justify-center cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={!!c.is_suppressed}
                          onChange={() => toggleSuppress(c)}
                          className="sr-only"
                          data-testid={`dns-${c.id}`}
                        />
                        <span
                          className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${c.is_suppressed ? "bg-[#DC2626] border-[#DC2626]" : "bg-white border-[#E5E5E5] group-hover:border-[#DC2626]"}`}
                          title={c.is_suppressed ? "Remove from suppression — email will be sent again" : "Mark as Do Not Send"}
                        >
                          {c.is_suppressed && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </span>
                      </label>
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <button onClick={(e) => { e.stopPropagation(); openEditor(c); }}
                              className="p-1 text-[#525252] hover:text-[#0055FF] mr-1"
                              title="Edit contact"
                              data-testid={`edit-contact-${c.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteOne(c); }}
                              className="p-1 text-[#525252] hover:text-[#DC2626]"
                              title="Delete contact"
                              data-testid={`delete-contact-${c.id}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex items-center justify-between mt-3 text-xs text-[#525252]">
        <span>Showing {rows.length === 0 ? 0 : offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
        <div className="flex gap-1">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  className="h-8 px-3 border border-[#E5E5E5] disabled:opacity-40" data-testid="prev-page">Previous</button>
          <button disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}
                  className="h-8 px-3 border border-[#E5E5E5] disabled:opacity-40" data-testid="next-page">Next</button>
        </div>
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setEditing(null)}>
          <div className="bg-white w-full max-w-xl border border-[#E5E5E5] shadow-2xl" onClick={e => e.stopPropagation()} data-testid="contact-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5]">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#0A0A0A]">
                {editing.id ? "Edit contact" : "New contact"}
              </h3>
              <button onClick={() => !saving && setEditing(null)} className="p-1 text-[#525252] hover:text-[#0A0A0A]" data-testid="contact-modal-close">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 text-xs">
              <label className="col-span-2 flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Email {!editing.id && <span className="text-[#DC2626]">*</span>}</span>
                <input
                  type="email"
                  value={editing.email || ""}
                  disabled={!!editing.id}
                  onChange={e => setEditing({ ...editing, email: e.target.value })}
                  placeholder="person@example.com"
                  className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF] disabled:bg-[#FAFAFA] disabled:text-[#A3A3A3]"
                  data-testid="contact-email-input"
                />
                {editing.id && <span className="text-[10px] text-[#A3A3A3]">Email cannot be changed after creation — delete and re-add if needed.</span>}
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">First name</span>
                <input value={editing.first_name || ""} onChange={e => setEditing({ ...editing, first_name: e.target.value })}
                       className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-first-name-input" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Last name</span>
                <input value={editing.last_name || ""} onChange={e => setEditing({ ...editing, last_name: e.target.value })}
                       className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-last-name-input" />
              </label>
              <label className="col-span-2 flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Company</span>
                <input value={editing.company_name || ""} onChange={e => setEditing({ ...editing, company_name: e.target.value })}
                       className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-company-input" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Job title</span>
                <input value={editing.job_title || ""} onChange={e => setEditing({ ...editing, job_title: e.target.value })}
                       className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-job-input" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Work phone</span>
                <input value={editing.phone || ""} onChange={e => setEditing({ ...editing, phone: e.target.value })}
                       placeholder="Desk / office line"
                       className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-phone-input" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Cell phone</span>
                <input value={editing.cell_phone || ""} onChange={e => setEditing({ ...editing, cell_phone: e.target.value })}
                       placeholder="Mobile / personal"
                       className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-cell-phone-input" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Country</span>
                <input value={editing.country || ""} onChange={e => setEditing({ ...editing, country: e.target.value })}
                       placeholder="USA"
                       className="h-9 px-3 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-country-input" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="font-bold uppercase tracking-wider text-[#525252] text-[10px]">Consent</span>
                <select value={editing.consent_status || "unknown"} onChange={e => setEditing({ ...editing, consent_status: e.target.value })}
                        className="h-9 px-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-[#0055FF]" data-testid="contact-consent-input">
                  <option value="unknown">Unknown</option>
                  <option value="opt_in">Opt-in (explicit)</option>
                  <option value="implicit">Implicit (client/lead)</option>
                  <option value="opt_out">Opt-out</option>
                </select>
              </label>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E5E5E5] bg-[#FAFAFA]">
              <button onClick={() => setEditing(null)} disabled={saving}
                      className="h-9 px-3 text-xs font-bold uppercase tracking-wider border border-[#E5E5E5] hover:border-[#525252] disabled:opacity-40" data-testid="contact-cancel">
                Cancel
              </button>
              <button onClick={saveContact} disabled={saving}
                      className="h-9 px-4 text-xs font-bold uppercase tracking-wider bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-40" data-testid="contact-save">
                {saving ? "Saving…" : (editing.id ? "Save changes" : "Create contact")}
              </button>
            </div>
          </div>
        </div>
      )}

    </MarketingShell>
  );
}
